import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { UserPermissionsResponse, ModulePermissionType } from "../../types/permissions";

export interface UserType {
    id: number
    name: string;
    email: string;
    empresa: {
        agencia?: string;
        contaBeneficiario?: string;
    };
    idUserType: number;
}

export interface CreateUserType {
    name: string;
    email: string;
    password: string;
}

export interface CreateEmpresaUserType {
    name: string;
    email: string;
    password: string;
    idUserType: number;
    idEmpresa: number;
}

export interface EditUserType {
    id: number;
    name: string;
    email: string;
    password: string;
}

export interface EditEmpresaUserType {
    id: number;
    name: string;
    email: string;
    password: string;
    agencia?: string;
    contaBeneficiario?: string;
    idUserType: number;
}

export default function useUser() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout } = useContext(AuthContext);
    const [listUsers, setListUsers] = useState<UserType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [initialListUsers, setInitialListUsers] = useState<UserType[]>([]);
    const [userPermissions, setUserPermissions] = useState<number[]>([]);
    const [useLegacyPermissions, setUseLegacyPermissions] = useState<boolean>(true);
    const [modulePermissions, setModulePermissions] = useState<ModulePermissionType[]>([]);
    const [user, setUser] = useState<UserType>({
        id: 0,
        name: "",
        email: "",
        empresa: {
            agencia: "",
            contaBeneficiario: "",
        },
        idUserType: 0,
    });
    function createEmpresaUser(createUserEmpresaType: CreateEmpresaUserType): Promise<CreateEmpresaUserType | null> {
        setLoading(true);
        return api
            .post(`/Users/createUsuariosEmpresa`, createUserEmpresaType)
            .then((response) => {
                const createdEmpresa: CreateEmpresaUserType = response.data;
                showToast("success", "Usuário criado com sucesso.");
                setLoading(false);
                return createdEmpresa;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar usuário. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar usuário:", error);
                return null;
            });
    }

    function getUsersPaged(
        pageNumber: number,
        Name?: string,
        IdEmpresa?: number,
        pageSize?: number,
    ): Promise<UserType[] | null> {
        setLoading(true);
        let url = `/Users/getUsersPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (Name) {
            url += `&Name=${Name}`;
        }

        if (IdEmpresa) {
            url += `&EmpresaId=${IdEmpresa}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListUsers(response.data.result);
                setInitialListUsers(response.data.result);
                setPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar usuários. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar usuários:", error);
                return null;
            });
    }

    function updateEmpresaUser(updateEmpresaUserData: EditEmpresaUserType): Promise<EditEmpresaUserType | null> {
        setLoading(true);
        return api
            .put(`/Users/${updateEmpresaUserData.id}`, updateEmpresaUserData)
            .then((response) => {
                showToast("success", "Usuário atualizado com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar usuário. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar usuário:", error);
                return null;
            });
    }

    function deleteEmpresaUser(userId: number): Promise<UserType | null> {
        setLoading(true);
        return api
            .delete(`/Users/${userId}`)
            .then(() => {
                setUser({
                    id: 0,
                    name: "",
                    email: "",
                    idUserType: 0,
                    empresa: {
                        agencia: "",
                        contaBeneficiario: "",
                    },
                });
                showToast("success", "Usuário deletado com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar usuário. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar usuário:", error);
                return null;
            });
    }

    async function toggleEmpresaStatus(empresaId: number) {
        setLoading(true);
        try {
            const response = await api.post(`/Empresas/toggle-status/${empresaId}`);
            setLoading(false);
            showToast("success", "Status da empresa alterado com sucesso!");
            return response.data;
        } catch (error: any) {
            setLoading(false);
            showToast("error", "Houve um erro ao altera o status da empresa, tente novamente.", error);
            throw new Error(error.response.data.message || "Erro desconhecido ao alternar status");
        }
    }

    function handleGetUserById(id: number): Promise<UserType | null> {
        setLoading(true);
        return api
            .get(`/Users/getById/${id}`)
            .then((response) => {
                const user: UserType = response.data;
                setUser(user);
                setLoading(false);
                return user;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar usupario pelo ID. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar usuário pelo ID:", error);
                return null;
            });
    }

    function getPermissionsByUser(userId: number): Promise<UserPermissionsResponse> {
        setLoading(true);
        return api
            .get<UserPermissionsResponse>(`/Users/${userId}/permissions`)
            .then((response) => {
                const data = response.data;
                setUseLegacyPermissions(data.useLegacyPermissions);
                setUserPermissions(data.legacyPermissions);
                setModulePermissions(data.modulePermissions);
                setLoading(false);
                return data;
            })
            .catch((error) => {
                setLoading(false);
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast(
                        "error",
                        "Erro ao obter permissões do usuário. Verifique novamente mais tarde.",
                        error
                    );
                }
                return { useLegacyPermissions: true, legacyPermissions: [], modulePermissions: [] };
            });
    }

    function validateCpf(cpf: string): Promise<{ message: string; tipo?: string; nome?: string; id?: number } | null> {
        setLoading(true);

        return api
            .get(`/Users/validate-cpf/${cpf}`)
            .then((response) => {
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                setLoading(false);

                if (error?.response?.status === 409) {
                    return error.response.data;
                }

                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao validar CPF. Verifique novamente mais tarde.", error);
                }

                console.error("Erro ao validar CPF:", error);
                return null;
            });
    }


    return {
        createEmpresaUser,
        updateEmpresaUser,
        deleteEmpresaUser,
        toggleEmpresaStatus,
        getUsersPaged,
        listUsers,
        pageCount,
        initialListUsers,
        handleGetUserById,
        user,
        setUser,
        getPermissionsByUser,
        userPermissions,
        useLegacyPermissions,
        modulePermissions,
        validateCpf,
    };
}